import {
  ExcelFileMetadata,
  ExcelTransaction,
  Organisation,
  ReviewStatus,
  TrackingCategory,
  ValidatedExcelTransaction,
} from '../models';
import dayjs from 'dayjs';
import { formatTransactionTableDate } from 'i18n/calendarLabels';
import {
  FetchAllExcelTransactionsSuccessAction,
  FetchExcelActivityLogsSuccessAction,
  FetchExcelFilesMetadataSuccessAction,
  FetchTrackingCategoriesSuccessAction,
  FetchTransactionItemsSuccessAction,
  FetchUploadedFileTransactionsSuccessAction,
  OrganisationState,
  UploadExcelTransactionsFailureAction,
  UploadExcelTransactionsSuccessAction,
} from '../types';

export function handleUploadExcelTransactionsRequest(
  state: OrganisationState
): OrganisationState {
  return {
    ...state,
    loading: true,
    uploadExcelTransactionsValidationData: undefined,
    uploadExcelTransactionsStatus: null,
    uploadedFileTransactionsPage: undefined,
  };
}

export function handleUploadExcelTransactionsSuccess(
  state: OrganisationState,
  action: UploadExcelTransactionsSuccessAction
): OrganisationState {
  const { importId, organisationId } = action.payload;
  return {
    ...state,
    loading: false,
    uploadExcelTransactionsValidationData: { importId, organisationId },
    uploadExcelTransactionsStatus: 'success',
    uploadedFileTransactionsPage: undefined,
  };
}

export function handleFetchUploadedFileTransactionsSuccess(
  state: OrganisationState,
  action: FetchUploadedFileTransactionsSuccessAction
): OrganisationState {
  const { transactions, total, page, pageSize } = action.payload;
  return {
    ...state,
    loading: false,
    uploadedFileTransactionsPage: {
      transactions,
      total,
      page,
      pageSize,
    },
  };
}

/** Keep table/drawer showing saved values until fetchAllExcelTransactions completes. */

export function mergeTransactionIntoAllExcelTransactionsPage(
  state: OrganisationState,
  transactionUuid: string,
  body: ExcelTransaction
): OrganisationState {
  const page = state.allExcelTransactionsPage;
  if (!page?.transactions?.length || !transactionUuid?.trim()) {
    return state;
  }
  const idx = page.transactions.findIndex(
    (t) => String(t.uuid) === String(transactionUuid)
  );
  if (idx < 0) return state;

  const transactions = page.transactions.slice();
  transactions[idx] = {
    ...transactions[idx],
    ...body,
    uuid: transactions[idx].uuid,
  };

  return {
    ...state,
    allExcelTransactionsPage: {
      ...page,
      transactions,
    },
  };
}

/** Snapshot current rows before an optimistic merge so FAILURE can restore them. */
export function snapshotTransactionsForRollback(
  state: OrganisationState,
  transactionUuids: string[]
): ValidatedExcelTransaction[] {
  const page = state.allExcelTransactionsPage;
  if (!page?.transactions?.length || !transactionUuids.length) return [];
  const wanted = new Set(
    transactionUuids.map((id) => String(id).trim()).filter(Boolean)
  );
  return page.transactions.filter((t) => wanted.has(String(t.uuid)));
}

export function restoreTransactionsFromRollback(
  state: OrganisationState,
  rollback: ValidatedExcelTransaction[] | null | undefined
): OrganisationState {
  const page = state.allExcelTransactionsPage;
  if (!page?.transactions?.length || !rollback?.length) {
    return {
      ...state,
      pendingExcelTransactionRollback: null,
    };
  }

  const byUuid = new Map(rollback.map((row) => [String(row.uuid), row]));
  const transactions = page.transactions.map(
    (row) => byUuid.get(String(row.uuid)) ?? row
  );

  return {
    ...state,
    allExcelTransactionsPage: {
      ...page,
      transactions,
    },
    pendingExcelTransactionRollback: null,
  };
}

export function applyOptimisticTransactionUpdate(
  state: OrganisationState,
  transactionUuid: string,
  body: ExcelTransaction
): OrganisationState {
  const rollback = snapshotTransactionsForRollback(state, [transactionUuid]);
  const merged = mergeTransactionIntoAllExcelTransactionsPage(
    state,
    transactionUuid,
    body
  );
  return {
    ...merged,
    pendingExcelTransactionRollback: [
      ...(state.pendingExcelTransactionRollback ?? []),
      ...rollback.filter(
        (row) =>
          !(state.pendingExcelTransactionRollback ?? []).some(
            (existing) => String(existing.uuid) === String(row.uuid)
          )
      ),
    ],
  };
}

export function applyOptimisticTransactionBatchUpdate(
  state: OrganisationState,
  items: { transactionUuid: string; body: ExcelTransaction }[]
): OrganisationState {
  const rollback = snapshotTransactionsForRollback(
    state,
    items.map((item) => item.transactionUuid)
  );
  const merged = mergeBatchTransactionsIntoAllExcelTransactionsPage(
    state,
    items
  );
  return {
    ...merged,
    pendingExcelTransactionRollback: [
      ...(state.pendingExcelTransactionRollback ?? []),
      ...rollback.filter(
        (row) =>
          !(state.pendingExcelTransactionRollback ?? []).some(
            (existing) => String(existing.uuid) === String(row.uuid)
          )
      ),
    ],
  };
}

export function mergeBatchTransactionsIntoAllExcelTransactionsPage(
  state: OrganisationState,
  items: { transactionUuid: string; body: ExcelTransaction }[]
): OrganisationState {
  return items.reduce(
    (next, item) =>
      mergeTransactionIntoAllExcelTransactionsPage(
        next,
        item.transactionUuid,
        item.body
      ),
    state
  );
}

export function handleFetchAllExcelTransactionsRequest(
  state: OrganisationState
): OrganisationState {
  return {
    ...state,
    allExcelTransactionsLoading: true,
  };
}

export function handleFetchAllExcelTransactionsFailure(
  state: OrganisationState
): OrganisationState {
  return {
    ...state,
    allExcelTransactionsLoading: false,
  };
}

const EXCEL_SERIAL_MIN = 1000;
const EXCEL_SERIAL_MAX = 100000;
const EXCEL_EPOCH_OFFSET = 25569;

/** Upload validation responses can return LocalDate as [year, month, day] with century -300. */
function correctUploadValidationYear(year: number): number {
  if (year >= 1680 && year < 1800) return year + 300;
  return year;
}

function parseLocalDateParts(
  value: unknown
): { year: number; month: number; day: number } | null {
  if (Array.isArray(value) && value.length >= 3) {
    const year = Number(value[0]);
    const month = Number(value[1]);
    const day = Number(value[2]);
    if (
      Number.isFinite(year) &&
      Number.isFinite(month) &&
      Number.isFinite(day)
    ) {
      return { year, month, day };
    }
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const localDateMatch = trimmed.match(
      /^(\d{3,4})\s*,\s*(\d{1,2})\s*,\s*(\d{1,2})$/
    );
    if (localDateMatch) {
      return {
        year: Number(localDateMatch[1]),
        month: Number(localDateMatch[2]),
        day: Number(localDateMatch[3]),
      };
    }
  }

  return null;
}

function formatDatePartsToIso(
  year: number,
  month: number,
  day: number
): string | null {
  const correctedYear = correctUploadValidationYear(year);
  const d = dayjs(new Date(correctedYear, month - 1, day));
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

function excelSerialToIso(value: number): string | null {
  const utcDays = Math.floor(value - EXCEL_EPOCH_OFFSET);
  const d = dayjs(new Date(utcDays * 86400 * 1000));
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

/** Normalise a single date field (YYYY-MM-DD or null when cleared). */
export function normalizeExcelTransactionDateField(
  value: unknown
): string | null {
  if (value == null || value === '') return null;

  const localDateParts = parseLocalDateParts(value);
  if (localDateParts) {
    return formatDatePartsToIso(
      localDateParts.year,
      localDateParts.month,
      localDateParts.day
    );
  }

  if (
    typeof value === 'number' &&
    value >= EXCEL_SERIAL_MIN &&
    value <= EXCEL_SERIAL_MAX
  ) {
    const fromSerial = excelSerialToIso(value);
    if (fromSerial) return fromSerial;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const compactMatch = trimmed.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compactMatch) {
    const year = Number(compactMatch[1]);
    const month = Number(compactMatch[2]);
    const day = Number(compactMatch[3]);
    const fromCompact = formatDatePartsToIso(year, month, day);
    if (fromCompact) return fromCompact;
  }

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashMatch) {
    const day = Number(slashMatch[1]);
    const month = Number(slashMatch[2]);
    let year = Number(slashMatch[3]);
    if (year < 100) year += year >= 70 ? 1900 : 2000;
    const fromSlash = formatDatePartsToIso(year, month, day);
    if (fromSlash) return fromSlash;
  }

  const d = dayjs(value as string | number | Date);
  if (d.isValid()) {
    const correctedYear = correctUploadValidationYear(d.year());
    if (correctedYear !== d.year()) {
      return d.year(correctedYear).format('YYYY-MM-DD');
    }
    return d.format('YYYY-MM-DD');
  }

  return trimmed || null;
}

/** Display helper for transaction tables (DD/MM/YYYY). */
export function formatExcelTransactionDateForDisplay(
  value: unknown,
  locale?: string | null
): string {
  const normalized = normalizeExcelTransactionDateField(value);
  if (!normalized) return '-';
  return formatTransactionTableDate(normalized, locale);
}

/** Map API row shapes and normalise date fields after fetch. */
export function normalizeExcelTransactionFromApi(
  row: ExcelTransaction
): ExcelTransaction {
  const raw = row as ExcelTransaction & {
    due_date?: unknown;
    exchange_rate?: unknown;
    discount_amount?: unknown;
    upload_date?: unknown;
    journal_modified_date?: unknown;
    linked_transaction_id?: unknown;
  };
  const dueDateRaw = row.dueDate ?? raw.due_date;
  const dueDate =
    dueDateRaw == null || dueDateRaw === ''
      ? null
      : normalizeExcelTransactionDateField(dueDateRaw);
  const exchangeRateRaw = row.exchangeRate ?? raw.exchange_rate;
  const exchangeRate =
    exchangeRateRaw == null || exchangeRateRaw === ''
      ? null
      : typeof exchangeRateRaw === 'number'
        ? exchangeRateRaw
        : Number.parseFloat(String(exchangeRateRaw).replace(/,/g, '.')) ||
          null;
  const discountAmountRaw = row.discountAmount ?? raw.discount_amount;
  const discountAmount =
    discountAmountRaw == null || discountAmountRaw === ''
      ? null
      : typeof discountAmountRaw === 'number'
        ? discountAmountRaw
        : Number.parseFloat(String(discountAmountRaw).replace(/,/g, '.')) ||
          null;
  const transactionDate =
    row.transactionDate == null || row.transactionDate === ''
      ? row.transactionDate
      : normalizeExcelTransactionDateField(row.transactionDate) ??
        row.transactionDate;
  const uploadDateRaw = row.uploadDate ?? raw.upload_date;
  const uploadDate =
    uploadDateRaw == null || uploadDateRaw === ''
      ? null
      : normalizeExcelTransactionDateField(uploadDateRaw);
  const journalModifiedRaw =
    row.journalModifiedDate ?? raw.journal_modified_date;
  const journalModifiedDate =
    journalModifiedRaw == null || journalModifiedRaw === ''
      ? null
      : journalModifiedRaw;
  const linkedTransactionRaw =
    row.linkedTransactionId ?? raw.linked_transaction_id;
  const linkedTransactionId =
    linkedTransactionRaw == null || linkedTransactionRaw === ''
      ? null
      : String(linkedTransactionRaw);
  return {
    ...row,
    transactionDate,
    dueDate,
    exchangeRate,
    discountAmount,
    uploadDate,
    journalModifiedDate,
    linkedTransactionId,
  };
}

/** Legacy Excel rows arrive with null reviewStatus; treat as reviewed and flag as Excel (not receipt). */

export function normalizeFetchedExcelTransactions(
  transactions: ValidatedExcelTransaction[]
): ValidatedExcelTransaction[] {
  return transactions.map((transaction) => {
    const normalized = normalizeExcelTransactionFromApi(transaction);
    if (normalized.reviewStatus != null) {
      return { ...normalized, errorReason: transaction.errorReason ?? '' };
    }
    return {
      ...normalized,
      errorReason: transaction.errorReason ?? '',
      reviewStatus: ReviewStatus.REVIEWED,
      isExcelTransaction: true,
    };
  });
}

export function handleFetchAllExcelTransactionsSuccess(
  state: OrganisationState,
  action: FetchAllExcelTransactionsSuccessAction
): OrganisationState {
  const { organisationId, transactions, total, page, pageSize } =
    action.payload;
  const normalizedTransactions =
    normalizeFetchedExcelTransactions(transactions);
  return {
    ...state,
    allExcelTransactionsLoading: false,
    allExcelTransactionsPage: {
      organisationId,
      transactions: normalizedTransactions,
      total,
      page,
      pageSize,
    },
  };
}

export function handleUploadExcelTransactionsFailure(
  state: OrganisationState,
  action: UploadExcelTransactionsFailureAction
): OrganisationState {
  const { validationData } = action.payload;
  const uploadExcelTransactionsStatus = validationData ? 'failure' : null;
  const normalizedValidationData =
    validationData?.validationErrors?.length
      ? {
          ...validationData,
          validationErrors: validationData.validationErrors.map((row) => ({
            ...normalizeExcelTransactionFromApi(row),
            errorReason: row.errorReason ?? '',
          })),
        }
      : validationData;
  return {
    ...state,
    loading: false,
    uploadExcelTransactionsValidationData: normalizedValidationData,
    uploadExcelTransactionsStatus,
  };
}

export function normalizeFetchedExcelFilesMetadata(
  files: ExcelFileMetadata[]
): ExcelFileMetadata[] {
  return files.map((file) => {
    if (file.fileType?.toUpperCase() !== 'XLSX') {
      return file;
    }
    return {
      ...file,
      reviewStatus: ReviewStatus.REVIEWED,
      isReviewed: true,
    };
  });
}

export function handleFetchExcelFilesMetadataSuccess(
  state: OrganisationState,
  action: FetchExcelFilesMetadataSuccessAction
): OrganisationState {
  const { organisationId, files } = action.payload;
  const normalizedFiles = normalizeFetchedExcelFilesMetadata(files);

  let organisations = state.organisations;
  if (organisations) {
    organisations = organisations.map((org) => {
      if (org.uuid === organisationId) {
        return {
          ...org,
          excelFilesMetadata: normalizedFiles,
        };
      }
      return org;
    });
  }

  return {
    ...state,
    organisations,
  };
}

export function handleFetchExcelActivityLogsSuccess(
  state: OrganisationState,
  action: FetchExcelActivityLogsSuccessAction
): OrganisationState {
  const { organisationId, activityLogs } = action.payload;

  if (!state.organisations) {
    return {
      ...state,
      loading: false,
    };
  }

  const organisations = state.organisations.map((org) =>
    org.uuid === organisationId
      ? {
          ...org,
          activityLogs,
        }
      : org
  );

  return {
    ...state,
    loading: false,
    organisations,
  };
}

export function applyPendingTrackingCategories(
  organisations: Organisation[] | undefined,
  pending: Record<string, TrackingCategory[]> | undefined
): {
  organisations: Organisation[] | undefined;
  pending: Record<string, TrackingCategory[]> | undefined;
} {
  if (!organisations?.length || !pending || Object.keys(pending).length === 0) {
    return { organisations, pending };
  }
  const remaining = { ...pending };
  const next = organisations.map((org) => {
    if (!(org.uuid in remaining)) return org;
    const trackingCategories = remaining[org.uuid];
    delete remaining[org.uuid];
    if (org.trackingCategories !== undefined) return org;
    return { ...org, trackingCategories };
  });
  const pendingNext =
    Object.keys(remaining).length > 0 ? remaining : undefined;
  return { organisations: next, pending: pendingNext };
}

export function handleFetchTrackingCategoriesSuccess(
  state: OrganisationState,
  action: FetchTrackingCategoriesSuccessAction
): OrganisationState {
  const { organisationId, trackingCategories } = action.payload;

  if (!state.organisations?.length) {
    return {
      ...state,
      pendingTrackingCategoriesByOrgId: {
        ...state.pendingTrackingCategoriesByOrgId,
        [organisationId]: trackingCategories,
      },
    };
  }

  let merged = false;
  const organisations = state.organisations.map((org) => {
    if (org.uuid !== organisationId) return org;
    merged = true;
    return {
      ...org,
      trackingCategories,
    };
  });

  if (!merged) {
    return {
      ...state,
      pendingTrackingCategoriesByOrgId: {
        ...state.pendingTrackingCategoriesByOrgId,
        [organisationId]: trackingCategories,
      },
    };
  }

  const pending = { ...state.pendingTrackingCategoriesByOrgId };
  delete pending[organisationId];

  return {
    ...state,
    organisations,
    pendingTrackingCategoriesByOrgId:
      Object.keys(pending).length > 0 ? pending : undefined,
  };
}

export function handleFetchTransactionItemsSuccess(
  state: OrganisationState,
  action: FetchTransactionItemsSuccessAction
): OrganisationState {
  const { organisationId, transactionItems } = action.payload;

  if (!state.organisations) {
    return {
      ...state,
      loading: false,
    };
  }

  const organisations = state.organisations.map((org) =>
    org.uuid === organisationId
      ? {
          ...org,
          transactionItems,
        }
      : org
  );

  return {
    ...state,
    loading: false,
    organisations,
  };
}
